import { PlusIcon } from '@heroicons/react/16/solid';
import { SparklesIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import store from 'app/store';
import clsx from 'clsx';
import Text from 'components/inputs/text';
import { ConfigurationType } from 'data/types';
import { generateConfigurations } from 'features/ai';
import {
  addNewConfiguration,
  generatePtacConfiguration,
  setActiveConfig,
  setConfigurations,
  updateConfigurationItem
} from 'features/configurations';
import { setActiveSelectedSystem, setSelectedSystems } from 'features/systems';
import { setActiveConfigurationStepId } from 'features/ui';
import useIsPtacPathway from 'hooks/use-is-ptac-pathway';
import { produce } from 'immer';
import { useCallback, useMemo, useState } from 'react';
import { getNextCustomConfigurationName } from 'utils/configuration-generation';
import {
  ConfigurationDotStatus,
  getConfigurationDotStatus
} from 'utils/configuration-status';
import { deleteConfigurations } from 'utils/data';
import {
  deleteConfigurationConfirmText,
  partitionConfigurationDeleteIds
} from 'utils/delete-configurations';
import { enableHorizontalScroll } from 'utils/helpers';
import { ptacGenerateDisabledReason } from 'utils/ptac-generation';
import { confirmationModal, notification } from 'utils/ui';

const RENAME_SIZE_LIMIT = 25;

const STATUS_TITLES: Record<ConfigurationDotStatus, string> = {
  valid: 'Valid',
  warning: 'Needs attention',
  error: 'Has errors'
};

type ConfigurationTabsProps = Readonly<{
  transparent?: boolean;
  onSelectConfiguration: (index: number) => void;
}>;

export default function ConfigurationTabs({
  transparent,
  onSelectConfiguration
}: ConfigurationTabsProps) {
  const dispatch = useAppDispatch();
  const { configurations, activeConfig, designRuleResults } = useAppSelector(
    (state) => state.configurations
  );
  const { selectedSystems } = useAppSelector((state) => state.systems);
  const { configGenerating } = useAppSelector((state) => state.ai);
  const structure = useAppSelector((state) => state.structure.structure);
  const isPtacPathway = useIsPtacPathway();
  const hasAiConfigs = configurations.some((config) => config.aiGenerated);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [newName, setNewName] = useState('');

  const ptacGenerateBlockedReason = useMemo(
    () => (isPtacPathway ? ptacGenerateDisabledReason(structure) : undefined),
    [isPtacPathway, structure]
  );
  const generateDisabled = isPtacPathway
    ? ptacGenerateBlockedReason !== undefined
    : configGenerating;
  const generateLabel = hasAiConfigs ? 'Regenerate' : 'Generate';

  const statuses = useMemo(
    () =>
      configurations.map((config, index) =>
        getConfigurationDotStatus(
          index,
          config,
          index === activeConfig,
          designRuleResults
        )
      ),
    [configurations, activeConfig, designRuleResults]
  );

  const handleStartRename = useCallback((config: ConfigurationType) => {
    setRenamingId(config.uniqueId);
    setNewName(config.name);
  }, []);

  const handleSaveName = useCallback(
    (config: ConfigurationType) => {
      const trimmed = newName.trim();
      if (trimmed !== '' && trimmed !== config.name) {
        dispatch(
          updateConfigurationItem({
            uniqueId: config.uniqueId,
            name: trimmed,
            modified: true
          })
        );
        dispatch(
          setSelectedSystems(
            produce(selectedSystems, (draft) => {
              draft.forEach((system) => {
                if (system.configuration.uniqueId === config.uniqueId) {
                  // eslint-disable-next-line no-param-reassign
                  system.configuration.name = trimmed;
                }
              });
            })
          )
        );
      }
      setRenamingId(null);
    },
    [newName, selectedSystems, dispatch]
  );

  const handleRenameKeyDown = useCallback(
    (key: string, config: ConfigurationType) => {
      if (key === 'Enter') {
        handleSaveName(config);
      } else if (key === 'Escape') {
        setRenamingId(null);
      }
    },
    [handleSaveName]
  );

  const renameConfigurations = useCallback((configs: ConfigurationType[]) => {
    let counter = 1;
    return configs.map((c) => {
      if (/^Configuration \d+$/.test(c.name)) {
        const name = `Configuration ${counter}`;
        counter += 1;
        return { ...c, name };
      }
      return c;
    });
  }, []);

  const addEmptyConfiguration = useCallback(
    async (existingConfigurations: ConfigurationType[]) => {
      const createdConfiguration = await dispatch(
        addNewConfiguration({
          name: getNextCustomConfigurationName(existingConfigurations)
        })
      ).unwrap();
      if (!createdConfiguration) return;

      const configurationIndex = store
        .getState()
        .configurations.configurations.findIndex(
          (configuration) =>
            configuration.uniqueId === createdConfiguration.uniqueId
        );
      if (configurationIndex < 0) return;

      dispatch(setActiveConfig(configurationIndex));
      dispatch(setActiveConfigurationStepId('condensers'));
    },
    [dispatch]
  );

  const handleAddConfiguration = useCallback(
    () => addEmptyConfiguration(configurations),
    [addEmptyConfiguration, configurations]
  );

  const handleDeleteConfiguration = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>, config: ConfigurationType) => {
      event.stopPropagation();

      const isLastConfiguration = configurations.length === 1;

      const deleteConfig = async () => {
        const remaining = configurations.filter(
          (conf) => conf.uniqueId !== config.uniqueId
        );
        dispatch(setConfigurations(renameConfigurations(remaining)));
        dispatch(
          setSelectedSystems(
            produce(selectedSystems, (draft) =>
              draft.filter(
                (system) => system.configuration.uniqueId !== config.uniqueId
              )
            )
          )
        );
        dispatch(setActiveConfig(0));
        dispatch(setActiveSelectedSystem(0));
        if (isLastConfiguration) {
          await addEmptyConfiguration(remaining);
        }
      };

      const confirmText = deleteConfigurationConfirmText({
        configuration: config,
        selectedSystems,
        isLastConfiguration
      });
      if (confirmText) {
        confirmationModal(confirmText, deleteConfig);
      } else {
        deleteConfig();
      }
    },
    [
      configurations,
      selectedSystems,
      dispatch,
      renameConfigurations,
      addEmptyConfiguration
    ]
  );

  const removeUnmodifiedAiConfigurations =
    useCallback(async (): Promise<boolean> => {
      const unmodifiedAiConfigs = configurations.filter(
        (config) => config.aiGenerated && !config.modified
      );
      if (unmodifiedAiConfigs.length === 0) return true;

      const { persistIds, localOnlyIds } = partitionConfigurationDeleteIds(
        unmodifiedAiConfigs.map((config) => config.uniqueId),
        store.getState().configurations.unsavedLocallyCreatedConfigurationIds
      );
      const { deletedUniqueIds, failedUniqueIds } =
        persistIds.length > 0
          ? await deleteConfigurations(persistIds)
          : { deletedUniqueIds: [], failedUniqueIds: [] };
      const removedUniqueIds = [...deletedUniqueIds, ...localOnlyIds];

      if (removedUniqueIds.length > 0) {
        const deletedIds = new Set(removedUniqueIds);
        const latestState = store.getState();
        const latestConfigurations = latestState.configurations.configurations;
        const activeConfigurationUniqueId =
          latestConfigurations[latestState.configurations.activeConfig]
            ?.uniqueId;
        const remainingConfigurations = latestConfigurations.filter(
          (configuration) => !deletedIds.has(configuration.uniqueId)
        );
        const nextActiveConfig = remainingConfigurations.findIndex(
          (configuration) =>
            configuration.uniqueId === activeConfigurationUniqueId
        );
        dispatch(setConfigurations(remainingConfigurations));
        dispatch(
          setSelectedSystems(
            latestState.systems.selectedSystems.filter(
              (system) => !deletedIds.has(system.configuration.uniqueId)
            )
          )
        );
        dispatch(setActiveConfig(nextActiveConfig >= 0 ? nextActiveConfig : 0));
        dispatch(setActiveSelectedSystem(0));
      }

      if (failedUniqueIds.length > 0) {
        notification(
          'Some configurations could not be deleted. Please try again.',
          'error'
        );
        return false;
      }
      return true;
    }, [configurations, dispatch]);

  const handleGenerateConfigurations = useCallback(async () => {
    const cleared = await removeUnmodifiedAiConfigurations();
    if (!cleared) return;

    if (isPtacPathway) {
      dispatch(generatePtacConfiguration());
      return;
    }
    await generateConfigurations();
  }, [removeUnmodifiedAiConfigurations, isPtacPathway, dispatch]);

  return (
    <div
      onWheel={enableHorizontalScroll}
      className={clsx(
        'flex items-center gap-1 overflow-x-auto overflow-y-hidden rounded-t-lg border-b border-gray-200 px-1',
        !transparent && 'bg-white'
      )}
    >
      {configurations.map((config, index) => (
        <div
          key={config.uniqueId}
          className={clsx(
            'group relative -mb-px flex shrink-0 items-center gap-1 border-b-2 transition-colors',
            index === activeConfig
              ? 'border-blue-500'
              : 'border-transparent hover:border-gray-300'
          )}
        >
          {renamingId === config.uniqueId ? (
            <div className='py-1 pl-2 pr-1'>
              <Text
                size='sm'
                onInput={(value) =>
                  setNewName((value ?? '').slice(0, RENAME_SIZE_LIMIT))
                }
                value={newName}
                onBlur={() => handleSaveName(config)}
                onKeyDown={(e) => handleRenameKeyDown(e.key, config)}
                autoFocus
                autoSelect
                controlled
              />
            </div>
          ) : (
            <>
              <button
                type='button'
                onClick={() => onSelectConfiguration(index)}
                onDoubleClick={() => handleStartRename(config)}
                className={clsx(
                  'whitespace-nowrap py-2 pl-4 text-sm font-medium',
                  index === activeConfig
                    ? 'text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                )}
              >
                {config.name}
              </button>
              <div className='relative mr-1 flex h-5 w-5 shrink-0 items-center justify-center'>
                <span
                  title={STATUS_TITLES[statuses[index]]}
                  className={clsx(
                    'absolute h-2 w-2 rounded-full transition-opacity group-hover:opacity-0',
                    statuses[index] === 'error' && 'bg-red-500',
                    statuses[index] === 'warning' && 'bg-amber-400',
                    statuses[index] === 'valid' && 'bg-emerald-500'
                  )}
                />
                <button
                  type='button'
                  onClick={(e) => handleDeleteConfiguration(e, config)}
                  className='absolute flex h-5 w-5 items-center justify-center rounded text-gray-300 opacity-0 transition-opacity hover:bg-gray-100 hover:text-gray-700 group-hover:text-gray-500 group-hover:opacity-100'
                >
                  <XMarkIcon className='h-3.5 w-3.5' />
                </button>
              </div>
            </>
          )}
        </div>
      ))}
      <button
        type='button'
        onClick={handleAddConfiguration}
        className='inline-flex shrink-0 items-center gap-1 whitespace-nowrap px-3 py-2 text-sm font-medium text-gray-400 transition-colors hover:text-gray-600'
      >
        <PlusIcon className='h-4 w-4' />
        New
      </button>
      {configGenerating && (
        <div className='flex shrink-0 items-center gap-2 px-3 py-2 text-sm font-medium text-gray-500'>
          <svg
            className='h-4 w-4 animate-spin'
            xmlns='http://www.w3.org/2000/svg'
            viewBox='0 0 512 512'
          >
            <path
              fill='gray'
              className='opacity-40'
              d='M256 64C150 64 64 150 64 256s86 192 192 192c70.1 0 131.3-37.5 164.9-93.6l.1 .1c-6.9 14.9-1.5 32.8 13 41.2c15.3 8.9 34.9 3.6 43.7-11.7c.2-.3 .4-.6 .5-.9l0 0C434.1 460.1 351.1 512 256 512C114.6 512 0 397.4 0 256S114.6 0 256 0c-17.7 0-32 14.3-32 32s14.3 32 32 32z'
            />
            <path
              fill='gray'
              d='M224 32c0-17.7 14.3-32 32-32C397.4 0 512 114.6 512 256c0 46.6-12.5 90.4-34.3 128c-8.8 15.3-28.4 20.5-43.7 11.7s-20.5-28.4-11.7-43.7c16.3-28.2 25.7-61 25.7-96c0-106-86-192-192-192c-17.7 0-32-14.3-32-32z'
            />
          </svg>
          Generating AI configurations...
        </div>
      )}
      <button
        type='button'
        onClick={handleGenerateConfigurations}
        disabled={generateDisabled}
        title={ptacGenerateBlockedReason}
        className={clsx(
          'ml-auto inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
          generateDisabled
            ? 'cursor-not-allowed text-gray-300'
            : 'text-blue-600 hover:bg-blue-50'
        )}
      >
        <SparklesIcon className='h-4 w-4' />
        {generateLabel}
      </button>
    </div>
  );
}
